import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import type { GuildDetailResponse, UpdateGuildPayload } from '@/lib/api';

/**
 * Guild-settings key for the DM-retention policy. Must match
 * `GUILD_DM_ON_MEMBER_LEAVE_KEY` in `@fias/db-types` — the server reads this
 * exact key and fails safe to 'keep' for anything it doesn't recognise.
 */
const DM_ON_MEMBER_LEAVE_KEY = 'dmOnMemberLeave';
type DmOnMemberLeave = 'keep' | 'archive';

function readDmPolicy(settings: Record<string, unknown> | undefined): DmOnMemberLeave {
  return settings?.[DM_ON_MEMBER_LEAVE_KEY] === 'archive' ? 'archive' : 'keep';
}

interface GuildSettingsFormProps {
  guild: GuildDetailResponse;
  onSave: (input: UpdateGuildPayload) => Promise<void>;
}

export function GuildSettingsForm({ guild, onSave }: GuildSettingsFormProps) {
  const [name, setName] = useState(guild.name);
  const [description, setDescription] = useState(guild.description || '');
  const [visibility, setVisibility] = useState(guild.visibility);
  const [maxMembers, setMaxMembers] = useState(guild.maxMembers?.toString() || '');
  const [dmPolicy, setDmPolicy] = useState<DmOnMemberLeave>(readDmPolicy(guild.settings));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form state when guild prop changes (e.g. after save or navigation)
  useEffect(() => {
    setName(guild.name);
    setDescription(guild.description || '');
    setVisibility(guild.visibility);
    setMaxMembers(guild.maxMembers?.toString() || '');
    setDmPolicy(readDmPolicy(guild.settings));
    setError(null);
  }, [
    guild.guildId,
    guild.name,
    guild.description,
    guild.visibility,
    guild.maxMembers,
    guild.settings,
  ]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Client-side validation
    if (name.trim().length < 2 || name.trim().length > 100) {
      setError('Name must be 2-100 characters');
      return;
    }
    if (maxMembers) {
      const parsed = parseInt(maxMembers, 10);
      if (isNaN(parsed) || parsed < 1 || !Number.isInteger(parsed)) {
        setError('Max members must be a positive integer');
        return;
      }
    }

    setSaving(true);
    setError(null);
    try {
      await onSave({
        name: name.trim(),
        description: description.trim(),
        visibility,
        maxMembers: maxMembers ? parseInt(maxMembers, 10) : null,
        // The server merges `settings` per-key, so send ONLY the key this form
        // owns — spreading the cached blob here would clobber keys another
        // client changed since our copy of `guild.settings` was fetched.
        settings: { [DM_ON_MEMBER_LEAVE_KEY]: dmPolicy },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="guild-name">Name</Label>
        <Input
          id="guild-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={100}
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="guild-description">Description</Label>
        <Textarea
          id="guild-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={2000}
          rows={4}
        />
      </div>
      <div className="space-y-2">
        <Label>Visibility</Label>
        <Select value={visibility} onValueChange={(v) => setVisibility(v as 'public' | 'private')}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="public">Public</SelectItem>
            <SelectItem value="private">Private</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="guild-max-members">Max Members (optional)</Label>
        <Input
          id="guild-max-members"
          type="number"
          value={maxMembers}
          onChange={(e) => setMaxMembers(e.target.value)}
          min={1}
          placeholder="No limit"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="guild-dm-policy">When a member leaves the guild</Label>
        <Select value={dmPolicy} onValueChange={(v) => setDmPolicy(v as DmOnMemberLeave)}>
          <SelectTrigger id="guild-dm-policy">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="keep">Keep their guild DMs as history</SelectItem>
            <SelectItem value="archive">Archive their guild DMs</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          Applies only to direct messages filed under this guild. Members’ personal chats with each
          other are never affected.
        </p>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <Button type="submit" disabled={saving}>
        {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
        Save Settings
      </Button>
    </form>
  );
}
